import { useCallback, useEffect, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { onCaptureOrganizationSettled } from '../organize-client';
import { reportError } from '../../lib/monitoring';

/**
 * The Locations that actually exist in one Project — M8 / S08-02.
 *
 * **THE OPTION LIST MUST NOT SHRINK WHEN A FILTER IS APPLIED**, which is why it
 * is read separately from the Capture list rather than derived from it.
 * Deriving the options from the currently displayed Captures would mean that
 * choosing "Unit 1207 Living Room" left that as the only option on screen, and
 * Brandon could never switch to another Location without clearing first. This
 * read is deliberately unaffected by the active filters.
 *
 * **IT READS `capture_search`, WHICH IS EXACTLY WHAT THAT TABLE IS FOR.** The
 * projection already holds one row per Capture with both the display text and
 * the normalized key, scoped by Project, behind the same owner-only RLS as
 * everything else (migration 0013). The alternative — loading every Capture in
 * the Project unfiltered just to read two columns off it — would be a bigger
 * query for a smaller answer.
 *
 * **THIS IS NOT A SECOND RETRIEVAL PATH.** It answers "what could I filter by
 * here", never "which Captures match". Deciding which Captures match is
 * `search_captures`, and there is only one of those.
 *
 * UI → THIS HOOK → `.select()` (ARCHITECTURE §2). It writes nothing.
 */

export interface CaptureLocationOption {
  /** The internal filter value. **Never rendered.** */
  readonly key: string;
  /** Brandon's own words, as accepted. This is what the screen shows. */
  readonly label: string;
}

export interface CaptureLocationsState {
  readonly options: CaptureLocationOption[];
  readonly reload: () => Promise<void>;
}

export function useCaptureLocations(projectId: string | undefined): CaptureLocationsState {
  const client = getSupabaseClient();
  const [options, setOptions] = useState<CaptureLocationOption[]>([]);

  const fetchOptions = useCallback(async (): Promise<CaptureLocationOption[]> => {
    if (!client || !projectId) return [];

    const { data, error } = await client
      .from('capture_search')
      .select('location_key, location_display')
      .eq('project_id', projectId)
      // A Capture with no Location is not an option, and it is not a problem
      // either: it stays visible in the list whenever no Location is selected.
      .not('location_key', 'is', null);

    if (error) {
      // **A MISSING OPTION LIST IS NOT A BROKEN SCREEN.** The Captures
      // themselves load through their own read; the worst case here is a
      // Location chip with nothing in it, which is better than a failed list.
      reportError(error, 'captures.locations');
      return [];
    }

    // ONE OPTION PER KEY. Two Captures can say "Unit 1207 Living Room" and
    // "unit 1207 living room"; they normalize to the same key and are one
    // place, so they are one option. The FIRST display text wins after
    // sorting, which makes the choice deterministic rather than dependent on
    // whichever row the database happened to return first.
    const byKey = new Map<string, string>();
    for (const row of data ?? []) {
      const key = row.location_key;
      const label = row.location_display;
      if (key === null || label === null) continue;
      if (!byKey.has(key)) byKey.set(key, label);
    }

    return [...byKey.entries()]
      .map(([key, label]) => ({ key, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [client, projectId]);

  useEffect(() => {
    let cancelled = false;
    void fetchOptions().then((next) => {
      if (!cancelled) setOptions(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchOptions]);

  /**
   * A Capture organized in the background can introduce a Location that did not
   * exist a moment ago, so this list catches up on the same signal the Capture
   * screens use (M7 post-closeout). It is a read; it can never organize
   * anything or spend a model call.
   */
  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onCaptureOrganizationSettled(() => {
      void fetchOptions().then((next) => {
        if (!cancelled) setOptions(next);
      });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [fetchOptions]);

  const reload = useCallback(async () => {
    setOptions(await fetchOptions());
  }, [fetchOptions]);

  return { options, reload };
}
